import { Product } from '../../domain/entities/Product';
import { ProductRepository } from '../../domain/repositories/ProductRepository';
import { CreateProductDto, ProductDto } from '../dtos/ProductDto';

export class CreateProductUseCase {
  constructor(private readonly productRepository: ProductRepository) {}

  async execute(createProductDto: CreateProductDto): Promise<ProductDto> {
    // Validate input
    await this.validateProduct(createProductDto);

    // Create domain entity
    const product = Product.create(
      createProductDto.name,
      createProductDto.description,
      createProductDto.category,
      createProductDto.images || [],
      createProductDto.ingredients || [],
      createProductDto.attributes || {},
      createProductDto.allergenInfo || { contains: [], mayContains: [] },
    );

    // Save product
    await this.productRepository.save(product);

    // Return DTO
    return {
      id: product.getId().getUniqueIdentifier(),
      name: product.getName(),
      description: product.getDescription(),
      category: product.getCategory(),
      images: product.getImages(),
      slug: product.getSlug(),
      ingredients: product.getIngredients(),
      attributes: product.getAttributes(),
      allergenInfo: product.getAllergenInfo(),
      availability: product.getAvailability(),
      isVisible: product.isVisible(),
      createdAt: product.getCreatedAt().toISOString(),
      updatedAt: product.getUpdatedAt().toISOString(),
      variants: [], // New product has no variants yet
    };
  }

  private async validateProduct(dto: CreateProductDto): Promise<void> {
    if (!dto.name?.trim()) {
      throw new Error('Product name is required');
    }

    if (!dto.description?.trim()) {
      throw new Error('Product description is required');
    }

    if (!dto.category?.trim()) {
      throw new Error('Product category is required');
    }

    // Check for duplicate name (optional business rule)
    const existingProducts = await this.productRepository.searchByName(dto.name);
    if (existingProducts.length > 0) {
      throw new Error(`Product with name "${dto.name}" already exists`);
    }
  }
}
